import type { AppState, CatalogCategory, CatalogResponse, CategoryKey, SeriesInfo } from "@/types";
import { isValidIsoDate, monthsAgoISO, yesterdayISO } from "@/lib/dates";

/** Sensible default series per category, used on first load and when switching category tabs. */
export const DEFAULT_SERIES: Record<CategoryKey, string> = {
  currency: "SEKUSDPMI",
  interest: "SECBREPOEFF",
  index: "SEKKIX92",
};

export function findCategory(catalog: CatalogResponse, key: CategoryKey): CatalogCategory | undefined {
  return catalog.categories.find((c) => c.key === key);
}

export function findSeries(
  catalog: CatalogResponse,
  seriesId: string
): { category: CategoryKey; series: SeriesInfo } | undefined {
  for (const category of catalog.categories) {
    const series = category.series.find((s) => s.id === seriesId);
    if (series) return { category: category.key, series };
  }
  return undefined;
}

/** Picks the preferred default series for a category, falling back to the first entry available. */
export function pickDefaultSeriesId(catalog: CatalogResponse, category: CategoryKey): string {
  const seriesList = findCategory(catalog, category)?.series ?? [];
  const preferred = DEFAULT_SERIES[category];
  if (seriesList.some((s) => s.id === preferred)) return preferred;
  return seriesList[0]?.id ?? preferred;
}

/** Combines whatever the URL provided with catalog validation and safe defaults into a committed AppState. */
export function resolveInitialState(urlState: Partial<AppState>, catalog: CatalogResponse): AppState {
  // A URL that names a series but omits (or misdeclares) its category should
  // still resolve to the series' real category, rather than being silently
  // dropped for not matching the (possibly-defaulted) category param.
  const resolvedFromSeries = urlState.series ? findSeries(catalog, urlState.series) : undefined;

  const category: CategoryKey = urlState.category ?? resolvedFromSeries?.category ?? "currency";
  const seriesList = findCategory(catalog, category)?.series ?? [];

  const seriesFromUrlValid = !!urlState.series && seriesList.some((s) => s.id === urlState.series);
  const series = seriesFromUrlValid ? urlState.series! : pickDefaultSeriesId(catalog, category);

  // Default the range end to yesterday, not today: Riksbank publishes
  // today's value late in the day, so a range ending "today" can never be
  // fully cached by the backend and re-fetches upstream on every visit.
  const to = isValidIsoDate(urlState.to) ? urlState.to : yesterdayISO();
  const fromCandidate = isValidIsoDate(urlState.from) ? urlState.from : monthsAgoISO(3, new Date(to));
  const from = fromCandidate <= to ? fromCandidate : to;

  return { category, series, from, to };
}
